import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';

export function MiningUserDetailsModal({ userDetails, isOpen, onClose, onStatusToggle, loading = false }) {
  const [statusLoading, setStatusLoading] = useState(false);

  const formatDate = (d) => d ? new Date(d).toLocaleString() : 'N/A';
  const fmt = (n) => (n === undefined || n === null) ? '0' : Number(n).toLocaleString();

  const handleStatusToggle = async (checked) => {
    setStatusLoading(true);
    try { await onStatusToggle(userDetails?.userId || 0, checked ? 'Active' : 'Inactive'); }
    finally { setStatusLoading(false); }
  };

  const StatusBadge = ({ status }) => {
    const map = {
      Active: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
      Inactive: 'border-red-500/30 bg-red-500/10 text-red-400',
      Pending: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400',
    };
    return (
      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${map[status] || 'border-border/70 text-muted-foreground'}`}>
        {status}
      </span>
    );
  };

  const Field = ({ label, value }) => (
    <div>
      <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1">{label}</p>
      <p className="text-sm font-mono text-foreground">{value}</p>
    </div>
  );

  if (!userDetails && !loading) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto border-border/70 bg-card">
        <DialogHeader>
          <DialogTitle className="font-serif italic text-lg text-foreground">User Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {loading ? (
            <p className="py-12 text-center text-sm text-muted-foreground">Loading user details...</p>
          ) : userDetails ? (
            <>
              {/* Header */}
              <div className="flex items-center gap-4 p-4 rounded-lg border border-border/40 bg-white/[0.02]">
                <div className="flex h-14 w-14 items-center justify-center rounded-full border border-ginox-green/30 bg-ginox-green/10 text-lg font-bold text-ginox-green">
                  {userDetails.name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground">{userDetails.name}</p>
                  <p className="text-sm font-mono text-muted-foreground">@{userDetails.username}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={userDetails.accountStatus} />
                    <Switch checked={userDetails.accountStatus === 'Active'} onCheckedChange={handleStatusToggle} disabled={statusLoading} />
                  </div>
                  {userDetails.minerStatus && <StatusBadge status={`Miner: ${userDetails.minerStatus}`} />}
                </div>
              </div>

              {/* Basic Info */}
              <div className="rounded-lg border border-border/40 bg-white/[0.02] p-4">
                <h3 className="text-xs font-semibold uppercase tracking-[1px] text-muted-foreground mb-4">Basic Information</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <Field label="User ID" value={userDetails.userId?.toString()} />
                  <Field label="Username" value={userDetails.username} />
                  <Field label="User Group" value={userDetails.userGroup || 'N/A'} />
                  <Field label="Platform Username" value={userDetails.preferences?.platformUsername || 'Not Set'} />
                </div>
              </div>

              {/* Mining Stats */}
              <div className="rounded-lg border border-border/40 bg-white/[0.02] p-4">
                <h3 className="text-xs font-semibold uppercase tracking-[1px] text-muted-foreground mb-4">Mining & Account Statistics</h3>
                <div className="grid grid-cols-3 gap-x-6 gap-y-4">
                  <Field label="Mining Level" value={userDetails.miningLevel?.toString() || '0'} />
                  <Field label="Sponsor Level" value={userDetails.sponsorLevel?.toString() || '0'} />
                  <Field label="Active Minings" value={fmt(userDetails.activeMinings)} />
                  <Field label="Completed Minings" value={fmt(userDetails.completedMinings)} />
                  <Field label="Cancelled Minings" value={fmt(userDetails.cancelledMinings)} />
                  <Field label="Invitation Count" value={fmt(userDetails.invitationCount)} />
                  <Field label="Login Reward" value={fmt(userDetails.loginReward)} />
                  <Field label="Tokens" value={fmt(userDetails.tokens)} />
                  <Field label="Mined Tokens" value={fmt(userDetails.minedTokens)} />
                  <Field label="Referral Mined" value={fmt(userDetails.referralMinedTokens)} />
                  <Field label="Daily Reward Tokens" value={fmt(userDetails.dailyRewardTokens)} />
                  <Field label="BNB" value={userDetails.bnb?.toString() || '0'} />
                  <Field label="Energy" value={userDetails.energy?.toString() || '0'} />
                </div>
              </div>

              {/* Energy Level */}
              {userDetails.energyLevel && (
                <div className="rounded-lg border border-border/40 bg-white/[0.02] p-4">
                  <h3 className="text-xs font-semibold uppercase tracking-[1px] text-muted-foreground mb-4">Energy Level Details</h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                    <Field label="Current Level" value={userDetails.energyLevel.levelId?.toString()} />
                    <Field label="Max Energy" value={fmt(userDetails.energyLevel.maxEnergy)} />
                    <Field label="Upgrade Cost" value={fmt(userDetails.energyLevel.upgradeCost)} />
                    <Field label="Next Level Cost" value={fmt(userDetails.energyLevel.nextLevelCost)} />
                  </div>
                </div>
              )}

              {/* App Opens */}
              {userDetails.appOpens && userDetails.appOpens.length > 0 && (
                <div className="rounded-lg border border-border/40 bg-white/[0.02] p-4">
                  <h3 className="text-xs font-semibold uppercase tracking-[1px] text-muted-foreground mb-4">App Opens History</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {userDetails.appOpens.map((ao) => (
                      <div key={ao._id} className="flex items-center justify-between rounded-md border border-border/40 px-3 py-2">
                        <div>
                          <p className="text-sm font-mono text-foreground">{ao.date ? new Date(ao.date).toLocaleDateString() : 'No Date'}</p>
                          <p className="text-xs text-muted-foreground">Opens: {ao.count}</p>
                        </div>
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${ao.energyGiven ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' : 'border-border/70 text-muted-foreground'}`}>
                          {ao.energyGiven ? 'Energy Given' : 'No Energy'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Account Timeline */}
              <div className="rounded-lg border border-border/40 bg-white/[0.02] p-4">
                <h3 className="text-xs font-semibold uppercase tracking-[1px] text-muted-foreground mb-4">Account Timeline</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <Field label="Created At" value={formatDate(userDetails.createdAt)} />
                  <Field label="Updated At" value={formatDate(userDetails.updatedAt)} />
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div className="flex justify-end pt-4 border-t border-border/40">
          <button
            onClick={onClose}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
